import {Color, ShaderMaterial, DoubleSide, Mesh, MeshStandardMaterial} from 'three';
import vertexShader from './grid.vert.glsl';
import fragmentShader from './grid.frag.glsl';
import { Texture } from 'three';
import Resources from '../../javascript/Resources';

export interface FloorGridShaderUniforms {
    gridSize: { value: number };
    lineWidth: { value: number };
    fadeRadius: { value: number };
    gridColor: { value: Color };
    backgroundColor: { value: Color };
    opacity: { value: number };
    cubeTexture: { value: Texture | null };
    textureOpacity: { value: number };
    hasTexture: { value: number };
}

export class FloorGridMaterialAlt extends MeshStandardMaterial {
    private gridUniforms = {
        gridSize: { value: 0.2 },
        lineWidth: { value: 1 },
        fadeRadius: { value: 5.0 },
        gridColor: { value: new Color('#813ad8') },
        backgroundColor: { value: new Color(0x000000) },
        gridOpacity: { value: 0.4 },
        cubeTexture: { value: null as Texture | null },
        textureOpacity: { value: 0.9 },
        hasTexture: { value: 0.0 }
    };

    public constructor(texture?: Texture) {
        super({
            color: new Color('#dbc3ff'),
            metalness: 0,
            roughness: 1,
            transparent: true,
            opacity: 1,
            side: DoubleSide,
            alphaTest: 0.01
        });

        if (texture) {
            this.gridUniforms.cubeTexture.value = texture;
            this.gridUniforms.hasTexture.value = 1.0;
        }

        this.onBeforeCompile = (shader) => {
            // Dodaj uniformy do shadera
            Object.assign(shader.uniforms, this.gridUniforms);

            // Modyfikuj vertex shader - dodaj varying
            shader.vertexShader = shader.vertexShader.replace(
                '#include <common>',
                `#include <common>
                varying vec2 vUv2;
                varying vec3 vWorldPos;`
            );

            shader.vertexShader = shader.vertexShader.replace(
                '#include <uv_vertex>',
                `#include <uv_vertex>
                vUv2 = uv;
                vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;`
            );

            // Modyfikuj fragment shader
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <common>',
                `#include <common>
                uniform float gridSize;
                uniform float lineWidth;
                uniform float fadeRadius;
                uniform vec3 gridColor;
                uniform vec3 backgroundColor;
                uniform float gridOpacity;
                uniform sampler2D cubeTexture;
                uniform float textureOpacity;
                uniform float hasTexture;
                
                varying vec2 vUv2;
                varying vec3 vWorldPos;`
            );

            // Zastąp obliczenia koloru przed cieniami
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <color_fragment>',
                `
                // Obliczenia siatki
                vec2 coord = (vUv2 - 0.5) * 10.0;
                float distanceFromCenter = length(coord);
                float circleFade = 1.0 - smoothstep(fadeRadius * 0.7, fadeRadius, distanceFromCenter);

                vec2 grid = abs(fract(coord / gridSize - 0.5) - 0.5) / fwidth(coord / gridSize);
                float line = min(grid.x, grid.y);
                float gridIntensity = 1.0 - smoothstep(lineWidth - 1.0, lineWidth, line);

                // Kolor bazowy z teksturą
                vec3 textureColor = backgroundColor;
                if (hasTexture > 0.5) {
                    textureColor = texture2D(cubeTexture, vUv2).rgb;
                }

                vec3 baseColor = mix(backgroundColor, textureColor, textureOpacity * circleFade);
                diffuseColor.rgb = mix(baseColor, gridColor, gridIntensity);
                
                // Ustaw alpha
                diffuseColor.a = max(gridIntensity, textureOpacity * 0.5) * circleFade * gridOpacity;
                
                #include <color_fragment>`
            );

            this.setLineWidth(1.2);

            this.userData.shader = shader;
        };
    }

    public setLineWidth(width: number): void {
        this.gridUniforms.lineWidth.value = width;
    }


    public updateTexture(texture: Texture | null): void {
        this.gridUniforms.cubeTexture.value = texture;
        this.gridUniforms.hasTexture.value = texture ? 1.0 : 0.0;
        this.needsUpdate = true;
    }

    public setGridColor(color: string | number | Color): void {
        if (typeof color === 'string' || typeof color === 'number') {
            this.gridUniforms.gridColor.value = new Color(color);
        } else {
            this.gridUniforms.gridColor.value = color;
        }
        this.needsUpdate = true;
    }

    public setBackgroundColor(color: string | number | Color): void {
        if (typeof color === 'string' || typeof color === 'number') {
            this.gridUniforms.backgroundColor.value = new Color(color);
        } else {
            this.gridUniforms.backgroundColor.value = color;
        }
        this.needsUpdate = true;
    }

    public setGridSize(size: number): void {
        this.gridUniforms.gridSize.value = size;
        this.needsUpdate = true;
    }

    public setFadeRadius(radius: number): void {
        this.gridUniforms.fadeRadius.value = radius;
        this.needsUpdate = true;
    }
}


export class FloorGridMaterial extends ShaderMaterial {
    public constructor(public texture?: Texture) {


        const uniforms: FloorGridShaderUniforms = {
            gridSize: { value: 0.2 },
            lineWidth: { value: 1 },
            fadeRadius: { value: 5.0 },
            gridColor: { value: new Color('#dbc3ff') },
            backgroundColor: { value: new Color(0x000000) },
            opacity: { value: 1 },
            cubeTexture: { value: texture },
            textureOpacity: { value: 0.9 },
            hasTexture: { value: texture ? 1.0 : 0.0 },
        };


        super({
            //@ts-ignore
            uniforms,
            vertexShader,
            fragmentShader,
            side: DoubleSide,
            transparent: true, 
            alphaTest: 0.01
        });
    }
}
